"use client";

import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { Text, TextSize, TextVariant, TextWeight } from "../../components";
import type { PopularCategory } from "../../types";

type PopularCategoriesProps = {
  popularCategories: PopularCategory[];
  selectedPage: "/odziez-dziecieca" | "/odziez-damska" | "/odziez-meska" | "/";
  isLoading?: boolean;
};

// Styled components z Footer
const TriangleIcon = styled.div<{ $expanded: boolean }>`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: ${(props) => (props.$expanded ? "6px solid black" : "none")};
  border-bottom: ${(props) => (!props.$expanded ? "6px solid black" : "none")};
  transition: transform 0.3s ease;
  transform: ${(props) =>
    props.$expanded ? "rotate(0deg)" : "rotate(-180deg)"};
`;

const AnimatedContent = styled.div<{ $expanded: boolean; $height: string }>`
  max-height: ${(props) => (props.$expanded ? props.$height : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  opacity: ${(props) => (props.$expanded ? "1" : "0")};
  transition-property: max-height, opacity;
  transition-duration: 0.3s;
  transition-timing-function: ease-in-out;
`;

// Skeleton Component
const PopularCategoriesSkeleton: React.FC = () => {
  const generateRandomWidth = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  return (
    <div className="w-full bg-gray-50 py-8 md:py-12">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6">
        {/* Title skeleton */}
        <div className="text-center mb-6 md:mb-8">
          <div className="h-8 bg-gray-300 animate-pulse rounded mx-auto w-48 md:w-64" />
        </div>

        {/* Categories grid skeleton */}
        <div className="w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
            {/* Column 1 */}
            <div className="space-y-3 text-center">
              {[...Array(6)].map((_, index) => (
                <div
                  key={`col-1-item-${index}`}
                  className="h-5 bg-gray-300 animate-pulse rounded mx-auto"
                  style={{ width: `${generateRandomWidth(60, 90)}%` }}
                />
              ))}
            </div>

            {/* Column 2 */}
            <div className="space-y-3 text-center">
              {[...Array(5)].map((_, index) => (
                <div
                  key={`col-2-item-${index}`}
                  className="h-5 bg-gray-300 animate-pulse rounded mx-auto"
                  style={{ width: `${generateRandomWidth(60, 90)}%` }}
                />
              ))}
            </div>

            {/* Column 3 */}
            <div className="space-y-3 text-center">
              {[...Array(6)].map((_, index) => (
                <div
                  key={`col-3-item-${index}`}
                  className="h-5 bg-gray-300 animate-pulse rounded mx-auto"
                  style={{ width: `${generateRandomWidth(60, 90)}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PopularCategories: React.FC<PopularCategoriesProps> = ({
  popularCategories,
  selectedPage,
  isLoading = false,
}) => {
  // State do śledzenia czy sekcja jest rozwinięta na mobile
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  // Find selected category using useMemo to avoid recalculation on each render
  const selectedCategory = useMemo(() => {
    return (
      popularCategories?.find((category) => category.url === selectedPage) ||
      null
    );
  }, [popularCategories, selectedPage]);

  // Toggle expansion
  const toggleExpansion = () => {
    setIsExpanded((prev) => !prev);
  };

  // Show skeleton when loading
  if (isLoading) {
    return <PopularCategoriesSkeleton />;
  }

  // If no category matches or categories array is empty, don't render anything
  if (!selectedCategory) return null;

  // Create subcategory columns
  const renderSubcategories = () => {
    const { subcategories } = selectedCategory;
    const itemsPerColumn = Math.ceil(subcategories.length / 3);

    // Create array of column arrays
    const columns = [0, 1, 2].map((colIndex) => {
      const startIdx = colIndex * itemsPerColumn;
      return subcategories.slice(startIdx, startIdx + itemsPerColumn);
    });

    return (
      <>
        {/* Desktop version - zawsze widoczny */}
        <div className="w-full hidden md:block">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
            {columns.map((column, colIdx) => (
              <div key={`col-${colIdx}`} className="space-y-3 text-center">
                {column.map((subcat, subIdx) => (
                  <a
                    key={`col-${colIdx}-item-${subIdx}`}
                    href={subcat.url.replace("https://carry.pl", "") || "#"}
                    className="block text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
                  >
                    <Text
                      text={subcat.name}
                      fontSize={TextSize.S}
                      weight={TextWeight.MEDIUM}
                    />
                  </a>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile version z animacją */}
        <div className="w-full md:hidden">
          <AnimatedContent $expanded={isExpanded} $height="1200px">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
              {columns.map((column, colIdx) => (
                <div key={`col-${colIdx}`} className="space-y-3 text-center">
                  {column.map((subcat, subIdx) => (
                    <a
                      key={`col-${colIdx}-item-${subIdx}`}
                      href={subcat.url.replace("https://carry.pl", "") || "#"}
                      className="block text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
                    >
                      <Text
                        text={subcat.name}
                        fontSize={TextSize.S}
                        weight={TextWeight.MEDIUM}
                      />
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </AnimatedContent>
        </div>
      </>
    );
  };

  return (
    <div className="w-full bg-gray-50 py-8 md:py-12">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6">
        {/* Header z możliwością kliknięcia na mobile */}
        <div
          className="flex justify-between items-center md:block cursor-pointer md:cursor-default mb-6 md:mb-8"
          onClick={toggleExpansion}
        >
          <Text
            text={selectedCategory.name}
            variant={TextVariant.H5}
            className="text-center"
          />
          <TriangleIcon $expanded={isExpanded} className="md:hidden" />
        </div>

        {renderSubcategories()}
      </div>
    </div>
  );
};

export default React.memo(PopularCategories);
